/**
 * Finalna, stabilna wersja animacji. Czyta treść z kontenera DIV
 * i wznawia animację od ostatniego aktywnego slajdu.
 * @param {string} selector - Selektor CSS dla kontenera animacji (np. '[data-resolver-container]').
 * @param {object} [customOptions={}] - Opcjonalne opcje.
 */
export function initTextResolverAnimation(selector, customOptions = {}) {
    const element = document.querySelector(selector);

    if (!element) {
        console.error(`Nie znaleziono elementu dla selektora: "${selector}"`);
        return;
    }

    const originalHTML = element.innerHTML.trim();
    if (!originalHTML) return;
    
    // Po odczytaniu treści, czyścimy kontener i czynimy go widocznym.
    // Jest pusty, więc nic nie "miga".
    element.innerHTML = '';
    element.style.visibility = 'visible'; // Usunięcie !important nie jest konieczne, styl inline wygra.

    const slideDelimiter = /<hr\s*\/?>/i;
    const texts = originalHTML.split(slideDelimiter).filter(text => text && text.trim() !== '');

    let animationIsActive = false;
    let animationIsRunning = false;
    let counter = 0;
    let slideTimeoutId = null;

    // --- Silnik animacji (wewnętrzny, stabilny) ---
    const resolver = {
        resolve: function resolve(options, callback) {
            const resolveString = options.resolveString;
            const tagsToSkip = ['<b>', '</b>', '<br>', '<br/>', '<br />', '<i>', '</i>'];

            function getRandomInteger(min, max) { return Math.floor(Math.random() * (max - min + 1)) + min; }
            function randomCharacter(characters) { return characters[getRandomInteger(0, characters.length - 1)]; }
            function isHTMLTag(str, index) { if (str[index] !== '<') return false; const endTagIndex = str.indexOf('>', index); if (endTagIndex === -1) return false; const tag = str.substring(index, endTagIndex + 1); return tagsToSkip.includes(tag); }

            function doRandomiserEffect(options, callback) {
                const { characters, element, partialString } = options;
                let iterations = options.iterations;

                setTimeout(() => {
                    if (!animationIsActive) return; // Mechanizm zatrzymywania
                    if (iterations >= 0) {
                        const nextOptions = { ...options, iterations: iterations - 1 };
                        if (iterations === 0) { element.innerHTML = partialString; } 
                        else {
                            let result = ''; let i = 0;
                            while (i < partialString.length) {
                                if (isHTMLTag(partialString, i)) { const tagEnd = partialString.indexOf('>', i) + 1; result += partialString.substring(i, tagEnd); i = tagEnd; } 
                                else { result += (i === partialString.length - 1) ? randomCharacter(characters) : partialString[i]; i++; }
                            }
                            element.innerHTML = result;
                        }
                        doRandomiserEffect(nextOptions, callback);
                    } else if (typeof callback === "function") {
                        callback();
                    }
                }, options.timeout);
            }

            function doResolverEffect(options, callback) {
                if (!animationIsActive) return;
                const { resolveString, offset } = options;
                let partialString = ''; let visibleCharCount = 0; let i = 0;

                while (i < resolveString.length && visibleCharCount <= offset) {
                    if (isHTMLTag(resolveString, i)) { const tagEnd = resolveString.indexOf('>', i) + 1; partialString += resolveString.substring(i, tagEnd); i = tagEnd; } 
                    else { partialString += resolveString[i]; visibleCharCount++; i++; }
                }

                doRandomiserEffect({ ...options, partialString }, () => {
                    if (!animationIsActive) return;
                    const nextOptions = { ...options, offset: offset + 1 };
                    const totalVisibleChars = resolveString.replace(/<[^>]*>/g, '').length;
                    if (visibleCharCount < totalVisibleChars) { doResolverEffect(nextOptions, callback); } 
                    else if (typeof callback === "function") { callback(); }
                });
            }
            doResolverEffect({ ...options, resolveString }, callback);
        }
    };

    const defaultOptions = {
        offset: 0,
        timeout: 10,
        iterations: 2,
        characters: ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'x', 'y', 'z', '#', '%', '&', '-'],
    };
    const options = { ...defaultOptions, ...customOptions, element };

    function animationCycle() {
        if (!animationIsActive) { animationIsRunning = false; return; }
        const currentOptions = { ...options, offset: 0, resolveString: texts[counter] };
        resolver.resolve(currentOptions, () => {
            if (!animationIsActive) return;
            slideTimeoutId = setTimeout(() => {
                counter = (counter + 1) % texts.length;
                animationCycle();
            }, 5500);
        });
    }

    // Opcjonalnie (options.scrollContainer): animacja rusza dopiero, gdy napis osiagnie docelowa pozycje.
    // - z options.settleTarget: gdy ten przodek (position: sticky) dojedzie do swojego `top` i sie zatrzyma,
    //   a scroll moze isc dalej (kolejne napisy),
    // - bez: gdy kontener jest doscrollowany do konca.
    const scroller = options.scrollContainer ? element.closest(options.scrollContainer) : null;
    const settleEl = scroller && options.settleTarget ? element.closest(options.settleTarget) : null;
    const atScrollEnd = () => {
        if (!scroller) return true;
        if (settleEl) {
            const pinTop = parseFloat(getComputedStyle(settleEl).top) || 0;
            return settleEl.getBoundingClientRect().top - scroller.getBoundingClientRect().top <= pinTop + 2;
        }
        return scroller.scrollTop >= scroller.scrollHeight - scroller.clientHeight - 2;
    };

    let isVisible = false;
    function update() {
        if (isVisible && atScrollEnd()) {
            animationIsActive = true;
            if (!animationIsRunning) {
                animationIsRunning = true;
                if (slideTimeoutId) clearTimeout(slideTimeoutId);
                animationCycle();
            }
        } else {
            animationIsActive = false;
            animationIsRunning = false;
            if (slideTimeoutId) {
                clearTimeout(slideTimeoutId);
                slideTimeoutId = null;
            }
            // Przy scrollContainer cofniecie scrolla czysci napis i zeruje animacje,
            // zeby po ponownym dociagnieciu ruszyla od poczatku.
            if (scroller) {
                element.innerHTML = '';
                counter = 0;
            }
        }
    }

    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => { isVisible = entry.isIntersecting; });
        update();
    }, {
        threshold: options.threshold ?? 0.5
    });

    observer.observe(element);
    if (scroller) scroller.addEventListener('scroll', update, { passive: true });
}